import type { LearningChunk } from "../../types";
import type { ChatbotConfidence, QueryProfiles, SourceSnippet } from "../types";
import type { QueryMatchContext } from "./query";
import { normalizeText } from "./query";

export function retrieveSnippets(
  chunks: LearningChunk[],
  matchContext: QueryMatchContext,
  maxSnippets: number,
  profiles: QueryProfiles
): { snippets: SourceSnippet[]; confidence: ChatbotConfidence } {
  const snippets = chunks
    .map((chunk) => scoreChunk(chunk, matchContext, profiles))
    .filter((snippet) => snippet.score > 0)
    .sort((a, b) => b.score - a.score || a.chunk.order - b.chunk.order)
    .slice(0, maxSnippets)
    .map((snippet, index) => ({
      ...snippet,
      relevanceLabel: index === 0 ? "Best match" : index < 3 ? "Related section" : "Broader context"
    })) as SourceSnippet[];

  return {
    snippets,
    confidence: confidenceForScore(snippets[0]?.score ?? 0)
  };
}

function scoreChunk(chunk: LearningChunk, matchContext: QueryMatchContext, profiles: QueryProfiles) {
  const query = matchContext.query;
  const heading = normalizeText(chunk.heading, profiles);
  const text = normalizeText(chunk.text, profiles);
  const keywords = normalizeText(chunk.keywords.join(" "), profiles);
  const topics = normalizeText(chunk.topics.join(" "), profiles);
  const allText = `${heading} ${text} ${keywords} ${topics}`;
  const headingMatches = query.terms.filter((term) => heading.includes(term)).length;
  const textMatches = query.expandedTerms.filter((term) => allText.includes(term)).length;
  const keywordMatches = query.terms.filter((term) => keywords.includes(term)).length;
  const topicScore = chunk.topics.reduce((total, topic) => total + (matchContext.topicScores.get(topic) ?? 0), 0);
  const objectiveScore = chunk.objectiveIds.reduce((total, objectiveId) => {
    const code = objectiveId.replace("objective-", "").replace("-", ".");
    return total + (matchContext.objectiveScores.get(code) ?? 0);
  }, 0);
  const facetScore = matchContext.matchedFacets.reduce((total, facetId) => {
    const facet = profiles.facets[facetId];
    const resourceTerms = facet?.resourceTerms ?? facet?.terms ?? [];
    const matches = resourceTerms.filter((term) => allText.includes(normalizeText(term, profiles))).length;
    return total + (matches > 0 ? Math.min(matches, 2) * Math.min(facet?.boost ?? 0, 8) : 0);
  }, 0);
  const exactPhrase = query.normalized.length > 4 && allText.includes(query.normalized);
  const score =
    headingMatches * 8 +
    (exactPhrase ? 7 : 0) +
    keywordMatches * 5 +
    topicScore +
    objectiveScore * 2 +
    facetScore +
    textMatches * 2;

  return { chunk, score, relevanceLabel: "Related section" };
}

function confidenceForScore(score: number): ChatbotConfidence {
  if (score >= 20) {
    return "high";
  }
  if (score >= 10) {
    return "medium";
  }
  return "low";
}
